import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CForm, CFormLabel, CFormInput, CFormTextarea,
  CButton, CRow, CCol, CAlert, CSpinner,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilArrowLeft } from '@coreui/icons';
import api from '../../services/api';

function digitCount(s) {
  return (String(s || '').match(/\d/g) || []).length;
}

export default function RecordPaymentPage() {
  const [search, setSearch] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  useEffect(() => {
    setResult(null);
    const d = digitCount(search);
    if (d < 4) {
      setCandidates([]);
      setLoadingCandidates(false);
      setSelectedInvoiceId(null);
      return undefined;
    }

    setLoadingCandidates(true);
    const t = setTimeout(() => {
      api
        .get('/finance/payment-candidates', { params: { q: search.trim() } })
        .then((r) => {
          setCandidates(r.data.candidates || []);
        })
        .catch(() => {
          setCandidates([]);
        })
        .finally(() => setLoadingCandidates(false));
    }, 400);

    return () => clearTimeout(t);
  }, [search]);

  const selectCandidate = useCallback((c) => {
    setSearch(c.invoice_no);
    setSelectedInvoiceId(c.id);
    const bal = Number(c.balance_due);
    if (bal > 0.0001) {
      setAmount(bal.toFixed(2));
    } else {
      setAmount('');
    }
    setError('');
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setResult(null);
    setSaving(true);
    try {
      const { data } = await api.post('/finance/payments', {
        search: search.trim(),
        amount: parseFloat(amount, 10),
        note: note.trim() || undefined,
      });
      setResult(data);
      setAmount('');
      setNote('');
      setSearch('');
      setCandidates([]);
      setSelectedInvoiceId(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Payment could not be recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol><h5 className="mb-0">Record payment</h5></CCol>
        <CCol xs="auto">
          <CButton color="secondary" variant="outline" size="sm" component={Link} to="/finance/payments">
            <CIcon icon={cilArrowLeft} className="me-1" /> Payment history
          </CButton>
        </CCol>
      </CRow>

      <CCard className="border-0 shadow-sm" style={{ borderRadius: 12 }}>
        <CCardHeader className="bg-white">Search invoice / job (type at least 4 digits)</CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleSubmit}>
            <CRow className="g-3">
              <CCol md={6}>
                <CFormLabel>Job ID, job code, or invoice no.</CFormLabel>
                <CFormInput
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setSelectedInvoiceId(null);
                  }}
                  placeholder="e.g. 2605, 1234, INV-20260502-0001"
                  autoComplete="off"
                />
                <div className="form-text">
                  Enter at least 4 digits to search for invoices.
                </div>
                {digitCount(search) >= 4 && (
                  <div className="mt-2 position-relative">
                    {loadingCandidates ? (
                      <div className="py-3 text-center text-muted small">
                        <CSpinner size="sm" className="me-2" /> Searching…
                      </div>
                    ) : candidates.length === 0 ? (
                      <div className="py-2 text-muted small">No matching invoices.</div>
                    ) : (
                      <div className="list-group list-group-flush shadow-sm border rounded bg-white" style={{ maxHeight: 280, overflowY: 'auto', zIndex: 5 }}>
                        {candidates.map((c) => {
                          const isSel = selectedInvoiceId === c.id;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              className="list-group-item list-group-item-action text-start py-2 px-3 border-0 border-bottom text-body"
                              style={isSel ? { backgroundColor: '#f0faf3', borderLeft: '4px solid #2eb85c' } : undefined}
                              onClick={() => selectCandidate(c)}
                            >
                              <div className="fw-semibold">{c.invoice_no}</div>
                              <div className="small text-secondary">
                                Job {c.jobcode} (ID {c.job_jid})
                                {c.customer_name ? ` · ${c.customer_name}` : ''}
                              </div>
                              <div className="small mt-1 text-body">
                                Total RM {c.total.toFixed(2)} · Paid RM {c.amount_paid.toFixed(2)} ·{' '}
                                <span className={c.balance_due > 0.01 ? 'text-warning fw-semibold' : 'text-success fw-semibold'}>
                                  Balance RM {c.balance_due.toFixed(2)}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CCol>
              <CCol md={6}>
                <CFormLabel>Amount received (RM)</CFormLabel>
                <CFormInput
                  type="number"
                  inputMode="decimal"
                  min="0.01"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
                <div className="form-text">Payment amount paid to the invoice</div>
              </CCol>
              <CCol xs={12}>
                <CFormLabel>Note (optional)</CFormLabel>
                <CFormTextarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </CCol>
            </CRow>
            {error && <CAlert color="danger" className="mt-3">{error}</CAlert>}
            {result && (
              <CAlert color="success" className="mt-3 mb-0">
                Payment recorded. Receipt ID <strong>{result.payment.id}</strong> for invoice{' '}
                <strong>{result.invoice.invoice_no}</strong>. Outstanding:{' '}
                <strong>RM {Number(result.balance_due).toFixed(2)}</strong>
                {' · '}
                <Link to={`/finance/payments/${result.payment.id}/receipt`}>View receipt (HTML)</Link>
              </CAlert>
            )}
            <div className="mt-3">
              <CButton
                color="success"
                type="submit"
                disabled={saving || !search.trim() || !amount || !(parseFloat(amount, 10) >= 0.01)}
              >
                {saving ? <CSpinner size="sm" /> : <><CIcon icon={cilSave} className="me-1" /> Record payment</>}
              </CButton>
            </div>
          </CForm>
        </CCardBody>
      </CCard>
    </>
  );
}
